import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useCart } from "../context/CartContext";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "http://localhost:89").replace(/\/$/, "");

const formatCurrency = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  minimumFractionDigits: 2,
});

const parseJsonResponse = async (response, fallbackMessage) => {
  const contentType = response.headers.get("content-type") || "";
  const text = await response.text();
  if (!response.ok || !contentType.includes("application/json")) {
    throw new Error(fallbackMessage);
  }
  return JSON.parse(text);
};

const STEP_FLOW = ["fiber", "resin", "flakes"];
const FLAKE_CATEGORY_CONFIG = [
  { key: "metalicos", label: "Metálicos" },
  { key: "tornasol", label: "Tornasol" },
  { key: "camaleon", label: "Camaleón" },
];
const normalizeString = (value) => String(value || "").trim();
const normalizeFiberCode = (value) => normalizeString(value).toUpperCase();
const normalizeFlakeCategory = (value) => {
  const normalized = normalizeString(value).toLowerCase();
  if (normalized === "camaleon") return "camaleon";
  if (normalized === "tornasol") return "tornasol";
  return "metalicos";
};
const normalizePositiveNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};
const normalizeBoolean = (value) => {
  const normalized = normalizeString(value).toLowerCase();
  if (["1", "true", "yes", "si"].includes(normalized)) return true;
  if (["0", "false", "no"].includes(normalized)) return false;
  return null;
};
const formatWeight = (value) =>
  Number(value || 0).toLocaleString("es-MX", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const SelectionIndicator = ({ selected, variant = "radio" }) => {
  if (variant === "checkbox") {
    return (
      <span
        className={`flex h-7 w-7 items-center justify-center border-2 ${
          selected ? "border-[#3164dc] bg-[#3164dc]" : "border-[#8aa0d4] bg-white"
        }`}
        aria-hidden="true"
      >
        {selected ? <span className="h-3 w-3 bg-white" /> : null}
      </span>
    );
  }

  return (
    <span
      className={`flex h-7 w-7 items-center justify-center rounded-full border-2 ${
        selected ? "border-[#3164dc]" : "border-[#8aa0d4]"
      }`}
      aria-hidden="true"
    >
      {selected ? <span className="h-3.5 w-3.5 rounded-full bg-[#3164dc]" /> : null}
    </span>
  );
};

const KitResinCard = ({
  imageUrl,
  title,
  description,
  selected,
  onClick,
  priceLabel,
  dimensionsLabel,
  grams,
  catalystGrams,
  optional = false,
  badge,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full rounded-2xl border bg-white p-3 text-left shadow-[0_1px_2px_rgba(0,0,0,0.04)] ${
      selected ? "border-[#3164dc]" : "border-[#c8c8c8]"
    }`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-3">
        <img
          src={imageUrl || "/ecommerce/Resinas.png"}
          alt={title}
          className="mt-1 h-16 w-16 shrink-0 object-contain"
        />
        <div className="min-w-0">
          <div className="flex items-start gap-2">
            <p className="text-[17px] font-bold leading-tight text-[#1f1f1f] md:text-[20px]">{title}</p>
          </div>
          {badge ? (
            <span className="mt-1 inline-flex rounded-full bg-[#1d1d1d] px-2 py-0.5 text-[10px] font-bold text-white">
              {badge}
            </span>
          ) : null}
          {description ? (
            <p className="mt-1 whitespace-pre-line text-[11px] leading-[1.2] text-[#5a5a5a] md:text-[13px]">
              {description}
            </p>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        {optional ? <span className="text-[11px] text-[#7d7d7d]">opcional</span> : <span className="h-[15px]" />}
        <SelectionIndicator selected={selected} variant={optional ? "checkbox" : "radio"} />
      </div>
    </div>
    <div className="mt-3 flex items-end justify-between gap-3 border-t border-[#d9d9d9] pt-2">
      <p className="text-[11px] leading-tight text-[#8a8a8a] md:text-xs">
        Para tu kit <span className="font-bold text-[#686868]">{dimensionsLabel}</span> calculamos:{" "}
        <span className="font-bold text-[#686868]">{formatWeight(grams)} g</span> · Catalizador{" "}
        <span className="font-bold text-[#686868]">{formatWeight(catalystGrams)} g</span>
      </p>
      <p className="shrink-0 text-[18px] font-semibold leading-none text-[#313131] md:text-[20px]">{priceLabel}</p>
    </div>
  </button>
);

const KitStepSummary = ({
  showNote = false,
  cm2,
  widthValue,
  heightValue,
  selectedFiber,
  selectedResin,
  selectedFlake,
  includeBlackResin,
  pricing,
  totalLabel,
  flakePendingLabel = "opcional",
}) => {
  const fiberLabel = selectedFiber
    ? `Fibra de carbono ${selectedFiber.type} (${widthValue}x${heightValue} cm)`
    : "Fibra de carbono";
  const fiberStatus = selectedFiber ? "completado" : "--";

  const selectedResinCode = pricing?.selected_resin?.code || selectedResin?.code || "";
  const resinLabel = selectedResinCode ? `Resina epóxica ${selectedResinCode}` : "Resina epóxica";
  const resinStatus = selectedResin ? "completado" : "siguiente paso";

  const blackResinStatus = includeBlackResin ? "completado" : "opcional";

  const flakesLabel = selectedFlake ? `Flakes ${selectedFlake.type}` : "Flakes (opcional)";
  const flakesStatus = selectedFlake ? "completado" : flakePendingLabel;

  return (
    <div className="space-y-2">
      {showNote && cm2 > 0 ? (
        <div className="rounded-xl border border-[#c9e8ff] bg-[#dff1ff] px-3 py-2 text-[#22506b]">
          <p className="text-[11px] leading-snug md:text-xs">
            Calculamos la cantidad suficiente de resina y catalizador para tus{" "}
            <span className="font-bold">{cm2.toLocaleString("es-MX")} cm²</span> de fibra. Recibes
            catalizador en proporción <span className="font-bold">2:1 (OM1 / OMUV2)</span> y{" "}
            <span className="font-bold">100:15 (RN1)</span> para que mezcles correctamente.
          </p>
        </div>
      ) : null}

      <div className="rounded-xl bg-[#ffffff] px-3 py-3 text-sm text-[#333]">
        <div className="space-y-1.5 border-b border-[#dcdcdc] pb-2">
          <div className="flex items-start justify-between gap-3">
            <span className="text-[#454545]">{fiberLabel}</span>
            <span className="shrink-0 text-right text-[#454545]">{fiberStatus}</span>
          </div>
          <div className="flex items-start justify-between gap-3">
            <span className="text-[#454545]">{resinLabel}</span>
            <span className="shrink-0 text-right text-[#454545]">{resinStatus}</span>
          </div>
          <div className="flex items-start justify-between gap-3">
            <span className="text-[#454545]">Resina negra RN1</span>
            <span className="shrink-0 text-right text-[#454545]">{blackResinStatus}</span>
          </div>
          <div className="flex items-start justify-between gap-3">
            <span className="text-[#8a8a8a]">{flakesLabel}</span>
            <span className="shrink-0 text-right text-[#8a8a8a]">{flakesStatus}</span>
          </div>
        </div>

        <div className="mt-2 flex items-center justify-between text-[#1f1f1f]">
          <span className="text-[18px] font-bold">Llevas</span>
          <span className="text-[18px] font-bold">{totalLabel}</span>
        </div>
      </div>
    </div>
  );
};

const EcommerceKitOmova = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addItem, openCart, shippingConfig } = useCart();

  const [step, setStep] = useState("fiber");
  const [fibers, setFibers] = useState([]);
  const [resins, setResins] = useState([]);
  const [flakes, setFlakes] = useState([]);
  const [blackResinOption, setBlackResinOption] = useState(null);
  const [constraints, setConstraints] = useState({ min_cm2: 600, max_cm2: 30000 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  const [selectedFiberCode, setSelectedFiberCode] = useState("");
  const [selectedResinId, setSelectedResinId] = useState("");
  const [selectedFlakeId, setSelectedFlakeId] = useState("");
  const [includeBlackResin, setIncludeBlackResin] = useState(false);
  const [activeFlakeCategory, setActiveFlakeCategory] = useState("metalicos");
  const [widthCm, setWidthCm] = useState("20");
  const [heightCm, setHeightCm] = useState("30");

  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingError, setPricingError] = useState("");
  const [resinPreviewMap, setResinPreviewMap] = useState({});
  const [blackResinPreview, setBlackResinPreview] = useState(null);
  const requestedFiberCode = useMemo(
    () => normalizeFiberCode(searchParams.get("fiber_code")),
    [searchParams]
  );
  const requestedWidth = useMemo(
    () => normalizePositiveNumber(searchParams.get("width_cm") || searchParams.get("width")),
    [searchParams]
  );
  const requestedHeight = useMemo(
    () => normalizePositiveNumber(searchParams.get("height_cm") || searchParams.get("height")),
    [searchParams]
  );
  const requestedResinId = useMemo(
    () => normalizePositiveNumber(searchParams.get("resin_id")),
    [searchParams]
  );
  const requestedIncludeBlackResin = useMemo(
    () => normalizeBoolean(searchParams.get("include_black_resin")),
    [searchParams]
  );
  const requestedSkipFlakes = useMemo(
    () => normalizeBoolean(searchParams.get("skip_flakes")),
    [searchParams]
  );
  const requestedFlakeId = useMemo(
    () => normalizePositiveNumber(searchParams.get("flake_id")),
    [searchParams]
  );

  const selectedFiber = useMemo(
    () => fibers.find((fiber) => fiber.code === selectedFiberCode) || null,
    [fibers, selectedFiberCode]
  );
  const selectedResin = useMemo(
    () => resins.find((resin) => resin.id === Number(selectedResinId)) || null,
    [resins, selectedResinId]
  );
  const selectedFlake = useMemo(
    () => flakes.find((flake) => flake.id === Number(selectedFlakeId)) || null,
    [flakes, selectedFlakeId]
  );
  const flakeCategoryCounts = useMemo(
    () =>
      FLAKE_CATEGORY_CONFIG.reduce((acc, category) => {
        acc[category.key] = flakes.filter(
          (flake) => normalizeFlakeCategory(flake.category) === category.key
        ).length;
        return acc;
      }, {}),
    [flakes]
  );
  const visibleFlakes = useMemo(
    () =>
      flakes.filter(
        (flake) => normalizeFlakeCategory(flake.category) === activeFlakeCategory
      ),
    [activeFlakeCategory, flakes]
  );
  const flakePreviewImage = useMemo(
    () => selectedFlake?.image_url || visibleFlakes[0]?.image_url || "/ecommerce/Flakes.png",
    [selectedFlake?.image_url, visibleFlakes]
  );

  const widthValue = Math.max(0, Number(widthCm) || 0);
  const heightValue = Math.max(0, Number(heightCm) || 0);
  const cm2 = widthValue * heightValue;
  const minCm2 = Number(constraints.min_cm2) || 600;
  const maxCm2 = Number(constraints.max_cm2) || 30000;
  const maxHeight = Number(selectedFiber?.max_height_cm) || 150;

  const areaTooSmall = cm2 > 0 && cm2 < minCm2;
  const areaTooLarge = cm2 > maxCm2;
  const heightTooLarge = heightValue > maxHeight;
  const dimensionsReady = widthValue > 0 && heightValue > 0 && !areaTooSmall && !areaTooLarge && !heightTooLarge;

  const isFiberStepReady = Boolean(selectedFiber && dimensionsReady);
  const isResinStepReady = Boolean(selectedResin && dimensionsReady);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile) return;

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, [isMobile, step]);

  useEffect(() => {
    let active = true;

    const loadOptions = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(`${API_BASE_URL}/api/ecommerce/kit-omova/options`);
        const data = await parseJsonResponse(response, "No se pudieron cargar las opciones de kit.");
        if (!active) return;

        const loadedFibers = Array.isArray(data?.fibers) ? data.fibers : [];
        const loadedResins = Array.isArray(data?.resins) ? data.resins : [];
        const loadedFlakes = Array.isArray(data?.flakes) ? data.flakes : [];

        setFibers(loadedFibers);
        setResins(loadedResins);
        setFlakes(loadedFlakes);
        setBlackResinOption(data?.black_resin_option || null);
        setConstraints(data?.constraints || { min_cm2: 600, max_cm2: 30000 });

        if (loadedFibers.length) setSelectedFiberCode((prev) => prev || loadedFibers[0].code);
        if (loadedResins.length) setSelectedResinId((prev) => prev || String(loadedResins[0].id));
      } catch (loadError) {
        if (!active) return;
        setError(loadError.message || "No se pudieron cargar las opciones de kit.");
      } finally {
        if (!active) return;
        setLoading(false);
      }
    };

    loadOptions();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (loading || !fibers.length || !resins.length) {
      return;
    }

    const matchedFiber = requestedFiberCode
      ? fibers.find((fiber) => normalizeFiberCode(fiber.code) === requestedFiberCode) || null
      : null;

    const nextFiber = matchedFiber || fibers[0] || null;
    if (!nextFiber) return;

    if (matchedFiber) {
      setSelectedFiberCode(matchedFiber.code);
    }

    const hasRequestedDimensions = Boolean(requestedWidth && requestedHeight);
    const nextWidth = hasRequestedDimensions ? requestedWidth : null;
    const nextHeight = hasRequestedDimensions ? requestedHeight : null;

    if (nextWidth) setWidthCm(String(nextWidth));
    if (nextHeight) setHeightCm(String(nextHeight));

    const maxAllowedHeight = Number(nextFiber.max_height_cm) || 150;
    const requestedArea = hasRequestedDimensions ? nextWidth * nextHeight : 0;
    const hasValidDimensions =
      hasRequestedDimensions &&
      requestedArea >= minCm2 &&
      requestedArea <= maxCm2 &&
      nextHeight <= maxAllowedHeight;

    if (!requestedResinId || !hasValidDimensions) {
      setSelectedFlakeId("");
      setIncludeBlackResin(false);
      setStep("fiber");
      return;
    }

    const matchedResin = resins.find((resin) => resin.id === requestedResinId) || null;
    if (!matchedResin) {
      setSelectedFlakeId("");
      setIncludeBlackResin(false);
      setStep("fiber");
      return;
    }

    setSelectedResinId(String(matchedResin.id));
    setIncludeBlackResin(requestedIncludeBlackResin === true);
    setSelectedFlakeId("");

    if (requestedSkipFlakes === true) {
      setStep("summary");
      return;
    }

    if (requestedFlakeId) {
      const matchedFlake = flakes.find((flake) => flake.id === requestedFlakeId) || null;
      if (matchedFlake) {
        setSelectedFlakeId(String(matchedFlake.id));
        setStep("summary");
        return;
      }
    }

    setStep("resin");
  }, [
    fibers,
    flakes,
    loading,
    maxCm2,
    minCm2,
    requestedFiberCode,
    requestedFlakeId,
    requestedHeight,
    requestedIncludeBlackResin,
    requestedResinId,
    requestedSkipFlakes,
    requestedWidth,
    resins,
  ]);

  useEffect(() => {
    if (!selectedFiber || !selectedResin || !dimensionsReady) {
      setPricing(null);
      setPricingError("");
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      setPricingLoading(true);
      setPricingError("");

      try {
        const response = await fetch(`${API_BASE_URL}/api/ecommerce/kit-omova/pricing`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            width_cm: widthValue,
            height_cm: heightValue,
            fiber_code: selectedFiber.code,
            resin_id: selectedResin.id,
            include_black_resin: includeBlackResin,
            flake_id: selectedFlake ? selectedFlake.id : null,
          }),
          signal: controller.signal,
        });

        const data = await parseJsonResponse(response, "No se pudo calcular el precio del kit.");
        setPricing(data);
      } catch (requestError) {
        if (requestError.name === "AbortError") return;
        setPricingError(requestError.message || "No se pudo calcular el precio del kit.");
      } finally {
        setPricingLoading(false);
      }
    }, 250);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [selectedFiber, selectedResin, selectedFlake, includeBlackResin, widthValue, heightValue, dimensionsReady]);

  useEffect(() => {
    if (!flakes.length) return;

    const hasCurrentCategory = flakes.some(
      (flake) => normalizeFlakeCategory(flake.category) === activeFlakeCategory
    );

    if (hasCurrentCategory) return;

    const firstAvailableCategory =
      FLAKE_CATEGORY_CONFIG.find((category) =>
        flakes.some((flake) => normalizeFlakeCategory(flake.category) === category.key)
      )?.key || "metalicos";

    setActiveFlakeCategory(firstAvailableCategory);
  }, [activeFlakeCategory, flakes]);

  useEffect(() => {
    if (!selectedFiber || !dimensionsReady || !resins.length) {
      setResinPreviewMap({});
      return;
    }

    const controller = new AbortController();

    const loadResinPreviews = async () => {
      try {
        const previews = await Promise.all(
          resins.map(async (resin) => {
            const response = await fetch(`${API_BASE_URL}/api/ecommerce/kit-omova/pricing`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                width_cm: widthValue,
                height_cm: heightValue,
                fiber_code: selectedFiber.code,
                resin_id: resin.id,
                include_black_resin: false,
                flake_id: null,
              }),
              signal: controller.signal,
            });

            const data = await parseJsonResponse(response, "No se pudo calcular la resina.");
            return [resin.id, data.selected_resin];
          })
        );

        setResinPreviewMap(Object.fromEntries(previews));
      } catch (requestError) {
        if (requestError.name === "AbortError") return;
        setResinPreviewMap({});
      }
    };

    loadResinPreviews();

    return () => controller.abort();
  }, [dimensionsReady, resins, selectedFiber, widthValue, heightValue]);

  useEffect(() => {
    if (!selectedFiber || !dimensionsReady || !blackResinOption || !selectedResin) {
      setBlackResinPreview(null);
      return;
    }

    const controller = new AbortController();

    const loadBlackResinPreview = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/ecommerce/kit-omova/pricing`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            width_cm: widthValue,
            height_cm: heightValue,
            fiber_code: selectedFiber.code,
            resin_id: selectedResin.id,
            include_black_resin: true,
            flake_id: null,
          }),
          signal: controller.signal,
        });

        const data = await parseJsonResponse(response, "No se pudo calcular la resina negra.");
        setBlackResinPreview(data.black_resin || null);
      } catch (requestError) {
        if (requestError.name === "AbortError") return;
        setBlackResinPreview(null);
      }
    };

    loadBlackResinPreview();

    return () => controller.abort();
  }, [blackResinOption, dimensionsReady, selectedFiber, selectedResin, widthValue, heightValue]);

  const summaryTotal = useMemo(() => {
    if (!pricing || !Number.isFinite(Number(pricing.final_cost))) return "--";
    return formatCurrency.format(Number(pricing.final_cost));
  }, [pricing]);

  const shippingLabel = useMemo(() => {
    const finalCost = Number(pricing?.final_cost);
    if (!Number.isFinite(finalCost)) return "--";
    const threshold = shippingConfig?.threshold ?? 499;
    const cost = shippingConfig?.cost ?? 199;
    return finalCost >= threshold ? "Gratis" : formatCurrency.format(cost);
  }, [pricing, shippingConfig]);

  const deliveryRangeLabel = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    const end = new Date(now);
    start.setDate(now.getDate() + 5);
    end.setDate(now.getDate() + 7);
    const formatter = new Intl.DateTimeFormat("es-MX", { month: "short", day: "numeric" });
    return `${formatter.format(start)} y ${formatter.format(end)}`;
  }, []);

  const currentStepIndex = STEP_FLOW.indexOf(step);
  const stepLabel = currentStepIndex === -1 ? "Resumen" : `Paso ${currentStepIndex + 1}/3`;
  const dimensionsLabel = `${widthValue} x ${heightValue} cm`;
  const summaryImages = useMemo(() => {
    const images = [
      selectedFiber
        ? {
            key: `fiber-${selectedFiber.code || selectedFiber.id || "default"}`,
            src: selectedFiber.image_url || "/ecommerce/Fibras.png",
            alt: selectedFiber.type || "Fibra",
            label: "Fibra",
          }
        : null,
      pricing?.selected_resin
        ? {
            key: `resin-${pricing.selected_resin.id || pricing.selected_resin.code || "default"}`,
            src: pricing.selected_resin.image_url || "/ecommerce/Resinas.png",
            alt: pricing.selected_resin.type || "Resina",
            label: pricing.selected_resin.code || "Resina",
          }
        : null,
      includeBlackResin && pricing?.black_resin
        ? {
            key: `black-resin-${pricing.black_resin.id || pricing.black_resin.code || "default"}`,
            src: pricing.black_resin.image_url || "/ecommerce/Resinas.png",
            alt: pricing.black_resin.type || "Resina negra",
            label: pricing.black_resin.code || "RN1",
          }
        : null,
      selectedFlake
        ? {
            key: `flake-${selectedFlake.id || selectedFlake.code || "default"}`,
            src: selectedFlake.image_url || "/ecommerce/Flakes.png",
            alt: selectedFlake.type || "Flakes",
            label: selectedFlake.code || "Flakes",
          }
        : null,
    ].filter(Boolean);

    return images;
  }, [includeBlackResin, pricing, selectedFiber, selectedFlake]);

  const goToNextStep = () => {
    if (step === "fiber") {
      if (!isFiberStepReady) return;
      setStep("resin");
      return;
    }
    if (step === "resin") {
      if (!isResinStepReady) return;
      setStep("flakes");
      return;
    }
    if (step === "flakes") {
      setStep("summary");
    }
  };

  const handleAddToCart = () => {
    if (!pricing || !selectedFiber || !selectedResin) return;

    addItem({
      type: "kit-omova",
      name: `KIT OMOVA ${selectedFiber.type} ${widthValue}x${heightValue} CM`,
      unitPrice: Number(pricing.final_cost) || 0,
      quantity: 1,
      unitEquivalent: includeBlackResin ? 3 : 2,
      payload: {
        fiber_code: selectedFiber.code,
        width_cm: widthValue,
        height_cm: heightValue,
        resin_id: selectedResin.id,
        include_black_resin: includeBlackResin,
        flake_id: selectedFlake ? selectedFlake.id : null,
      },
    });

    openCart();
  };

  const handleResetKit = () => {
    if (fibers.length) setSelectedFiberCode(fibers[0].code);
    if (resins.length) setSelectedResinId(String(resins[0].id));
    setSelectedFlakeId("");
    setIncludeBlackResin(false);
    setWidthCm("20");
    setHeightCm("30");
    setStep("fiber");
  };

  return (
    <main className="bg-[#efefef] px-3 pb-6 pt-3 md:px-6 md:pt-5">
      <div className="mx-auto max-w-[760px] overflow-hidden rounded-2xl border border-[#d2d2d2] bg-[#f4f4f4]">
        <section className="bg-[radial-gradient(circle_at_top,#0e3036_0%,#071820_55%,#041117_100%)] px-4 py-3 text-white md:px-6">
          <div className="flex items-center justify-between">
            <span aria-hidden="true" className="block h-9 md:h-10" />
            <button
              type="button"
              onClick={() => navigate("/ecommerce")}
              className="text-3xl leading-none text-white/80"
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>
        </section>

        <section className="px-4 pb-5 pt-4 md:px-6">
          <div className="flex items-center justify-between text-[#8e8e8e]">
            <button
              type="button"
              onClick={() => {
                if (step === "resin") setStep("fiber");
                if (step === "flakes") setStep("resin");
                if (step === "summary") setStep("flakes");
                if (step === "fiber") navigate("/ecommerce");
              }}
              className="text-2xl"
            >
              ← Atrás
            </button>
            <p className="text-lg font-semibold text-[#4c4c4c]">{step === "summary" ? "Resumen" : "Kit fibra Omova"}</p>
            <span className="text-sm">{stepLabel}</span>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-3">
            {[0, 1, 2].map((index) => (
              <div
                key={index}
                className={`h-1.5 rounded-full ${
                  (currentStepIndex >= index || (step === "summary" && index === 2))
                    ? "bg-primary-green"
                    : "bg-[#b8b8b8]"
                }`}
              />
            ))}
          </div>

          {loading && <p className="mt-4 text-sm text-[#666]">Cargando opciones...</p>}
          {!loading && error && <p className="mt-4 text-sm font-semibold text-red-600">{error}</p>}

          {!loading && !error && step === "fiber" && (
            <div className="mt-4 space-y-4">
              <div>
                <h2 className="text-3xl font-bold text-[#1f1f1f] md:text-4xl">Elige la fibra</h2>
                <p className="text-sm text-[#666]">Revisa las opciones y toca para cambiar.</p>
              </div>

              <div className="rounded-xl border border-[#c8c8c8] bg-white p-3">
                <img
                  src={selectedFiber?.image_url || "/ecommerce/Fibras.png"}
                  alt={selectedFiber?.type || "Fibra"}
                  className="h-52 w-full object-contain md:h-64"
                />
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-xl font-bold text-[#1f1f1f] md:text-2xl">{selectedFiber?.type || "Fibra"}</p>
                  <p className="text-xs text-[#666]">Alto Máx: {maxHeight} cm</p>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#666]">Opciones</p>
                <div className="grid grid-cols-3 gap-2 md:grid-cols-4">
                  {fibers.map((fiber) => (
                    <button
                      key={fiber.code}
                      type="button"
                      onClick={() => setSelectedFiberCode(fiber.code)}
                      className={`overflow-hidden rounded-lg border ${
                        selectedFiberCode === fiber.code
                          ? "border-primary-green ring-1 ring-primary-green"
                          : "border-[#c8c8c8]"
                      } bg-white`}
                    >
                      <img src={fiber.image_url || "/ecommerce/Fibras.png"} alt={fiber.type} className="h-16 w-full object-cover" />
                      <p className="px-1 py-1 text-[10px] font-semibold uppercase text-[#2f2f2f]">{fiber.type}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-[#d5d5d5] bg-[#f0f0ed] p-3">
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-[#6a6a6a]">ALTO</p>
                    <input
                      type="number"
                      min="1"
                      value={heightCm}
                      onChange={(event) => setHeightCm(event.target.value)}
                      className="mt-1 w-full rounded-lg border border-[#c8c8c8] bg-white px-2 py-1 text-xl font-bold text-primary-green"
                    />
                  </div>
                  <p className="pb-2 text-xl font-bold">x</p>
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-[#6a6a6a]">LARGO</p>
                    <input
                      type="number"
                      min="1"
                      value={widthCm}
                      onChange={(event) => setWidthCm(event.target.value)}
                      className="mt-1 w-full rounded-lg border border-[#c8c8c8] bg-white px-2 py-1 text-xl font-bold text-primary-green"
                    />
                  </div>
                </div>
                <p className="mt-2 text-center text-sm text-[#444]">
                  Área: <span className="font-bold">{cm2.toLocaleString("es-MX")} cm²</span>
                </p>
                {areaTooSmall && <p className="mt-2 text-xs font-semibold text-red-600">Área mínima: {minCm2} cm².</p>}
                {areaTooLarge && <p className="mt-2 text-xs font-semibold text-red-600">Área máxima: {maxCm2} cm².</p>}
                {heightTooLarge && (
                  <p className="mt-2 text-xs font-semibold text-red-600">Esta fibra permite hasta {maxHeight} cm de alto.</p>
                )}
              </div>

              <div className="rounded-xl border border-[#c9e8ff] bg-[#dff1ff] px-4 py-3 text-[#22506b]">
                <p className="text-sm font-bold">¿Necesitas referencia?</p>
                <p className="mt-1 text-xs leading-relaxed md:text-sm">
                  21,600 cm² cubren el cofre de un auto compacto (120 x 180 cm). 2,500 cm²
                  cubren un volante (125 x 20 cm).
                </p>
              </div>

              <KitStepSummary
                cm2={cm2}
                widthValue={widthValue}
                heightValue={heightValue}
                selectedFiber={selectedFiber}
                selectedResin={selectedResin}
                selectedFlake={selectedFlake}
                includeBlackResin={includeBlackResin}
                pricing={pricing}
                totalLabel={summaryTotal}
                flakePendingLabel="opcional"
              />

              <button
                type="button"
                onClick={goToNextStep}
                disabled={!isFiberStepReady || pricingLoading || !!pricingError}
                className="w-full rounded-lg bg-primary-green py-3 text-2xl font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continuar a resina →
              </button>
            </div>
          )}

          {!loading && !error && step === "resin" && (
            <div className="mt-4 space-y-4">
              <div>
                <h2 className="text-3xl font-bold text-[#1f1f1f] md:text-4xl">Elige la resina</h2>
                <p className="text-sm text-[#666]">
                  Resina epóxica que une, protege y da rigidez a tu pieza con fibra de carbono.
                </p>
              </div>

              <div className="space-y-2">
                {resins.map((resin) => {
                  const isActive = Number(selectedResinId) === resin.id;
                  const resinPreview =
                    resinPreviewMap[resin.id] ||
                    (Number(pricing?.selected_resin?.id) === resin.id ? pricing?.selected_resin : null);
                  const resinPriceLabel = resinPreview
                    ? formatCurrency.format(Number(resinPreview.total || 0))
                    : "--";

                  return (
                    <KitResinCard
                      key={resin.id}
                      onClick={() => setSelectedResinId(String(resin.id))}
                      imageUrl={resin.image_url}
                      title={`${resin.code} · ${resin.type}`}
                      description={resin.description}
                      selected={isActive}
                      priceLabel={resinPriceLabel}
                      dimensionsLabel={dimensionsLabel}
                      grams={resinPreview?.grams || 0}
                      catalystGrams={resinPreview?.catalyst_grams || 0}
                    />
                  );
                })}
              </div>

              {blackResinOption ? (
                <KitResinCard
                  imageUrl={blackResinOption.image_url || pricing?.black_resin?.image_url}
                  title={`${blackResinOption.code} · ${blackResinOption.type}`}
                  description={blackResinOption.description || pricing?.black_resin?.description || "También llamado Gel Coat que se utiliza como base para pegar la fibra a tu pieza."}
                  selected={includeBlackResin}
                  optional
                  badge="Para forrar"
                  onClick={() => setIncludeBlackResin((value) => !value)}
                  priceLabel={
                    blackResinPreview
                      ? `+ ${formatCurrency.format(Number(blackResinPreview.total || 0))}`
                      : "No incluido"
                  }
                  dimensionsLabel={dimensionsLabel}
                  grams={blackResinPreview?.grams || 0}
                  catalystGrams={blackResinPreview?.catalyst_grams || 0}
                />
              ) : null}

              <KitStepSummary
                showNote
                cm2={cm2}
                widthValue={widthValue}
                heightValue={heightValue}
                selectedFiber={selectedFiber}
                selectedResin={selectedResin}
                selectedFlake={selectedFlake}
                includeBlackResin={includeBlackResin}
                pricing={pricing}
                totalLabel={summaryTotal}
                flakePendingLabel="siguiente paso"
              />

              <button
                type="button"
                onClick={goToNextStep}
                disabled={!isResinStepReady || pricingLoading || !!pricingError}
                className="w-full rounded-lg bg-primary-green py-3 text-2xl font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continuar a flakes →
              </button>
            </div>
          )}

          {!loading && !error && step === "flakes" && (
            <div className="mt-4 space-y-4">
              <div>
                <h2 className="text-3xl font-bold text-[#1f1f1f] md:text-4xl">¿Agregar flakes?</h2>
                <p className="text-sm text-[#666]">
                  Hojuelas de colores metálicos, opcionales para darles un estilo único a tus piezas.
                </p>
              </div>

              <div className="overflow-hidden rounded-2xl border border-[#c8c8c8] bg-white">
                <div className="h-[170px] bg-[#111] p-2 md:h-[220px]">
                  <img
                    src={flakePreviewImage}
                    alt="Vista previa flakes"
                    className="h-full w-full rounded-xl object-cover"
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = "/ecommerce/Flakes.png";
                    }}
                  />
                </div>
              </div>

              <div>
                <p className="text-base font-bold uppercase text-[#1f1f1f]">
                  {flakes.length} colores disponibles
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {FLAKE_CATEGORY_CONFIG.map((category) => {
                    const count = flakeCategoryCounts[category.key] || 0;
                    if (!count) return null;

                    const isActive = activeFlakeCategory === category.key;
                    return (
                      <button
                        key={category.key}
                        type="button"
                        onClick={() => {
                          setActiveFlakeCategory(category.key);

                          if (
                            selectedFlake &&
                            normalizeFlakeCategory(selectedFlake.category) !== category.key
                          ) {
                            setSelectedFlakeId("");
                          }
                        }}
                        className={`rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wide ${
                          isActive
                            ? "border-primary-green text-primary-green"
                            : "border-[#bdbdbd] text-[#6a6a6a]"
                        }`}
                      >
                        {category.label} ({count})
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {visibleFlakes.map((flake) => (
                  <button
                    key={flake.id}
                    type="button"
                    onClick={() => setSelectedFlakeId(String(flake.id))}
                    className={`overflow-hidden rounded-xl border bg-white text-center ${
                      Number(selectedFlakeId) === flake.id
                        ? "border-primary-green ring-1 ring-primary-green"
                        : "border-[#c8c8c8]"
                    }`}
                  >
                    <div className="flex h-[110px] items-center justify-center bg-[#fafafa] p-2">
                      <img
                        src={flake.image_url || "/ecommerce/Flakes.png"}
                        alt={flake.type}
                        className="h-full w-full object-contain"
                      />
                    </div>
                    <div className="border-t border-[#ececec] px-2 py-2">
                      <p className="text-sm font-semibold text-[#1f1f1f]">{flake.type}</p>
                    </div>
                  </button>
                ))}
              </div>

              <KitStepSummary
                cm2={cm2}
                widthValue={widthValue}
                heightValue={heightValue}
                selectedFiber={selectedFiber}
                selectedResin={selectedResin}
                selectedFlake={selectedFlake}
                includeBlackResin={includeBlackResin}
                pricing={pricing}
                totalLabel={summaryTotal}
                flakePendingLabel="opcional"
              />

              <div className="space-y-3">
                <button
                  type="button"
                  onClick={goToNextStep}
                  disabled={!selectedFlake}
                  className="w-full rounded-lg bg-primary-green py-3 text-2xl font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {selectedFlake
                    ? `Agregar flakes (+ ${formatCurrency.format(Number(selectedFlake.base_price || 0))}) →`
                    : "Selecciona un flake"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedFlakeId("");
                    setStep("summary");
                  }}
                  className="w-full rounded-lg border border-[#bdbdbd] bg-white py-3 text-xl font-semibold text-[#1f1f1f]"
                >
                  No, continuar al resumen
                </button>
              </div>
            </div>
          )}

          {!loading && !error && step === "summary" && (
            <div className="mt-4 space-y-4">
              <div>
                <h2 className="text-4xl font-extrabold text-[#1f1f1f]">¡Listo! Revisa tu kit</h2>
                <p className="text-sm text-[#666]">Confirma medidas y cantidades antes de agregarlo al carrito.</p>
              </div>

              {summaryImages.length ? (
                <div className={`grid gap-3 ${summaryImages.length >= 4 ? "grid-cols-4" : summaryImages.length === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
                  {summaryImages.map((image) => (
                    <div
                      key={image.key}
                      className="overflow-hidden rounded-xl border border-[#d8d8d8] bg-white"
                    >
                      <div className="flex h-[88px] items-center justify-center bg-[#f8f8f8] p-2 md:h-[110px]">
                        <img
                          src={image.src}
                          alt={image.alt}
                          className="h-full w-full object-contain"
                          onError={(event) => {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = image.label === "Fibra" ? "/ecommerce/Fibras.png" : image.label === "Flakes" ? "/ecommerce/Flakes.png" : "/ecommerce/Resinas.png";
                          }}
                        />
                      </div>
                      <div className="border-t border-[#ececec] px-2 py-1 text-center text-[11px] font-semibold uppercase tracking-wide text-[#585858] md:text-xs">
                        {image.label}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="rounded-xl border border-[#c8c8c8] bg-[#f5f5f5] p-4">
                <h3 className="text-xl font-bold uppercase text-[#555]">Tu kit incluye</h3>
                <div className="mt-3 space-y-3 text-left text-[#1f1f1f]">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="text-xl font-bold">Fibra {selectedFiber?.type || "--"}</p>
                      <p className="text-sm text-[#555]">{widthValue} x {heightValue} cm · {cm2.toLocaleString("es-MX")} cm²</p>
                    </div>
                    <button type="button" onClick={() => setStep("fiber")} className="shrink-0 text-base font-bold text-primary-green">
                      Editar
                    </button>
                  </div>

                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="text-xl font-bold">{pricing?.selected_resin?.code || "--"} · {pricing?.selected_resin?.type || "Resina"}</p>
                      <p className="text-sm text-[#555]">
                        {Number(pricing?.selected_resin?.grams || 0).toLocaleString("es-MX")} g + catalizador {Number(pricing?.selected_resin?.catalyst_grams || 0).toLocaleString("es-MX")} g
                      </p>
                    </div>
                    <button type="button" onClick={() => setStep("resin")} className="shrink-0 text-base font-bold text-primary-green">
                      Editar
                    </button>
                  </div>

                  {includeBlackResin && (
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p className="text-xl font-bold">RN1 · Resina Negra</p>
                        <p className="text-sm text-[#555]">
                          {Number(pricing?.black_resin?.grams || 0).toLocaleString("es-MX")} g + catalizador {Number(pricing?.black_resin?.catalyst_grams || 0).toLocaleString("es-MX")} g
                        </p>
                      </div>
                      <button type="button" onClick={() => setStep("resin")} className="shrink-0 text-base font-bold text-primary-green">
                        Editar
                      </button>
                    </div>
                  )}

                  {selectedFlake && (
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p className="text-xl font-bold">Flakes {selectedFlake.type}</p>
                        <p className="text-sm text-[#555]">Bote {Number(pricing?.flake?.grams || 5) || 5} g</p>
                      </div>
                      <button type="button" onClick={() => setStep("flakes")} className="shrink-0 text-base font-bold text-primary-green">
                        Editar
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-xl bg-[#dff1ff] px-3 py-2 text-sm text-[#31516d]">
                Las cantidades están calculadas para tus <b>{cm2.toLocaleString("es-MX")} cm²</b> siguiendo la regla por m² de fibra.
              </div>

              <div className="rounded-xl border border-[#d0d0d0] bg-white p-3">
                <div className="flex items-center justify-between text-base">
                  <span>Subtotal (IVA incluido):</span>
                  <b className="text-2xl">{summaryTotal}</b>
                </div>
                <div className="mt-1 flex items-center justify-between text-base">
                  <span>Envío:</span>
                  <b className="text-primary-green">{shippingLabel}</b>
                </div>
              </div>

              <div className="inline-flex items-center gap-3 bg-black px-3 py-1.5 text-sm font-semibold text-white md:text-base">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-5 w-5 shrink-0"
                >
                  <rect x="1" y="3" width="15" height="13" />
                  <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                  <circle cx="5.5" cy="18.5" r="1.5" />
                  <circle cx="18.5" cy="18.5" r="1.5" />
                </svg>
                <span>Tu pedido llegaría entre {deliveryRangeLabel}</span>
              </div>

              {pricingError && <p className="text-sm font-semibold text-red-600">{pricingError}</p>}

              <button
                type="button"
                onClick={handleAddToCart}
                disabled={!pricing || pricingLoading}
                className="w-full rounded-full bg-primary-green py-3 text-3xl font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Añadir al carrito
              </button>
              <button
                type="button"
                onClick={handleResetKit}
                className="w-full rounded-full border border-[#a0a0a0] bg-white py-2 text-2xl font-semibold text-[#2e2e2e]"
              >
                Configurar otro kit
              </button>

              <div className="pt-2 text-center text-xs text-[#666]">
                <Link to="/ecommerce/checkout" className="font-semibold text-primary-green underline">
                  Ir a checkout
                </Link>
              </div>
            </div>
          )}

          {pricingError && step !== "summary" && (
            <p className="mt-3 text-sm font-semibold text-red-600">{pricingError}</p>
          )}
        </section>
      </div>
    </main>
  );
};

export default EcommerceKitOmova;
